import { createContext, useState, useEffect, useContext, useRef } from 'react';
import useApi from '../hooks/api/useApi';
import { ConfigContext } from '../contexts/ConfigContext';
import { setBusinessTimezone } from '../utils/businessTime';
import clearSession from '../utils/clearSession';

export const AuthContext = createContext(null);

const IDLE_MS = 60 * 60 * 1000; // 60 min → auto logout
const WARN_MS = 55 * 60 * 1000; // 55 min → show warning

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('accessToken'));
  const { onReset, mode } = useContext(ConfigContext);
  // Read at sign-out time, which may be an idle timer set long before.
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const {
    data: meData,
    error,
    isLoading: meLoading,
    refresh: refreshMe
  } = useApi(token ? 'me' : null, {
    revalidateOnMount: true,
    revalidateIfStale: true
  });

  const [user, setUser] = useState(() => {
    const cached = localStorage.getItem('auth_user');
    return cached ? JSON.parse(cached) : null;
  });
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [countdown, setCountdown] = useState(300);

  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntRef = useRef(null);
  const isWarningRef = useRef(false);

  useEffect(() => {
    if (meData) {
      setUser(meData?.data);
      localStorage.setItem('auth_user', JSON.stringify(meData?.data));
    }
  }, [meData]);

  // The business clock every screen judges "today" by (utils/businessTime).
  // Set during render, before the children render, so even the first paint
  // from the cached user uses it. Idempotent.
  setBusinessTimezone(user?.business_time?.timezone || user?.restaurant?.timezone || null);

  const clearAllTimers = () => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownIntRef.current);
  };

  const login = ({ access_token, refresh_token }) => {
    localStorage.setItem('accessToken', access_token);
    // The handoff flow issues a personal access token with no refresh token.
    // Storing null would persist the string "null", which is truthy — it slips
    // past axiosClient's `if (!refreshToken)` guard and gets sent to the refresh
    // endpoint on the first 401. Remove the key instead.
    if (refresh_token) {
      localStorage.setItem('refreshToken', refresh_token);
    } else {
      localStorage.removeItem('refreshToken');
    }
    // Drop the previous session's cached user so the dashboard never builds its
    // permission-gated sidebar from a stale snapshot. Starting from null forces
    // the known-good empty -> fresh transition, and refreshMe() pulls the
    // authoritative /me straight away instead of waiting on a background revalidate.
    localStorage.removeItem('auth_user');
    setUser(null);
    setToken(access_token);
    refreshMe();
  };

  const logout = () => {
    clearAllTimers();
    isWarningRef.current = false;
    clearSession();
    setUser(null);
    setToken(null);
    setShowIdleWarning(false);
    onReset({ mode: modeRef.current });
  };

  const resetTimers = () => {
    if (isWarningRef.current) return;

    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);

    warnTimerRef.current = setTimeout(() => {
      isWarningRef.current = true;
      setShowIdleWarning(true);
      setCountdown(300);
      countdownIntRef.current = setInterval(() => {
        setCountdown((prev) => Math.max(0, prev - 1));
      }, 1000);
    }, WARN_MS);

    logoutTimerRef.current = setTimeout(() => {
      clearAllTimers();
      isWarningRef.current = false;
      clearSession();
      setUser(null);
      setToken(null);
      setShowIdleWarning(false);
      onReset({ mode: modeRef.current });
      window.location.href = '/login';
    }, IDLE_MS);
  };

  const stayLoggedIn = () => {
    clearInterval(countdownIntRef.current);
    isWarningRef.current = false;
    setShowIdleWarning(false);
    resetTimers();
  };

  // Idle timer — only active while logged in
  useEffect(() => {
    if (!token) {
      clearAllTimers();
      setShowIdleWarning(false);
      isWarningRef.current = false;
      return;
    }

    let throttle = null;
    const onActivity = () => {
      if (isWarningRef.current) return;
      if (throttle) return;
      throttle = setTimeout(() => {
        throttle = null;
        resetTimers();
      }, 500);
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearAllTimers();
      if (throttle) clearTimeout(throttle);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const businessType = user?.restaurant?.business_type?.slug ?? null;

  // null  = not applicable (staff / super admin / not loaded)
  // false = merchant_admin, Stripe NOT connected
  // true  = merchant_admin, Stripe connected
  const stripeConnected = user?.restaurant?.stripe_onboarding_complete ?? null;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        refreshMe,
        isLoadingUser: token && !meData && !error,
        login,
        logout,
        token,
        meLoading,
        permissions: Array.isArray(user?.permissions) ? user.permissions : [],
        businessType,
        stripeConnected,
        // Modules the merchant switched off in Settings → Feature Settings, so
        // the sidebar can drop their entries. refreshMe (after a save) updates it.
        disabledModules: Array.isArray(user?.disabled_modules) ? user.disabled_modules : []
      }}
    >
      {children}

      {showIdleWarning && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '14px',
              padding: '36px 32px',
              maxWidth: '380px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 24px 64px rgba(0,0,0,0.25)'
            }}
          >
            <div style={{ fontSize: '42px', marginBottom: '10px' }}>⏱️</div>
            <h3 style={{ margin: '0 0 8px', fontSize: '19px', fontWeight: 700, color: '#111' }}>Session Expiring Soon</h3>
            <p style={{ margin: '0 0 4px', color: '#555', fontSize: '14px', lineHeight: 1.5 }}>
              You've been inactive for a while. You'll be logged out in:
            </p>
            <div
              style={{
                fontSize: '40px',
                fontWeight: 700,
                color: '#ef4444',
                margin: '18px 0',
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}
            >
              {formatCountdown(countdown)}
            </div>
            <button
              onClick={stayLoggedIn}
              style={{
                background: '#111',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '13px 0',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Stay Logged In
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}
